import React, { type MouseEvent, type KeyboardEvent, useCallback } from 'react';
import { isMacOs } from '@jira/platform__os-detection/src/isMacOs.tsx';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ShortcutIcon from '@atlaskit/icon/core/link-external';
import { Flex, xcss } from '@atlaskit/primitives';
import {
	type CommandRowProps,
	type Command,
	CommandActionType,
} from '@atlassian/jira-command-palette/src/common/types/commands/index.tsx';
import { components } from '@atlassian/jira-command-palette/src/common/ui/components/index.tsx';
import { isSecondaryTrigger } from '@atlassian/jira-command-palette/src/common/utils.tsx';
import { useActionsCommandPalette } from '@atlassian/jira-command-palette/src/controllers/command-palette/index.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { useRouter } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import { IconContainer, NameContainer } from '../../ui/name-container/index.tsx';
import { NavigationLink } from '../../ui/navigation-link/index.tsx';
import { NewTabHintMessage } from '../../ui/new-tab-hint-message/index.tsx';
import { isCurrentPage } from '../is-current-page/index.tsx';
import { ICONS_CONTAINER_TEST_ID, NAVIGATION_LINK_TEST_ID } from './constants.tsx';
import type {
	CommandToTransform,
	HideAndTransformNavigationCommandType,
	TransformLinkPath,
	TransformNavigationCommandType,
	UseHideCurrentPathCommandReturn,
} from './types.tsx';

export const useTransformNavigationCommands = (): UseHideCurrentPathCommandReturn => {
	const [
		{
			location: { pathname },
		},
		{ push, pushTo },
	] = useRouter();

	const { getAnalyticsAttributes } = useActionsCommandPalette();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const openNewTab = useCallback((link: TransformLinkPath, openBackground?: boolean) => {
		const url = typeof link === 'string' ? link : link.path;

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		const newTab = window.open(url, '_blank');

		if (openBackground) {
			newTab?.blur();

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.focus();
		}

		return newTab;
	}, []);

	const openSameTab = useCallback(
		(link: TransformLinkPath) => {
			if (typeof link === 'string') {
				return push(link);
			}
			return pushTo(link);
		},
		[push, pushTo],
	);

	const renderCommand = useCallback(
		(props: CommandRowProps, path: TransformLinkPath, shouldAlwaysOpenNewTab?: boolean) => {
			const { onClick, ...rest } = props;
			const { command } = rest;

			const handleClick = (event: MouseEvent | KeyboardEvent) => {
				const wasSecondaryTriggered = isSecondaryTrigger(event);
				if (!wasSecondaryTriggered) {
					onClick?.(event); // Run main action as it will close the modal too
					event.preventDefault();
					return;
				}
				const analytics = getAnalyticsAttributes(
					command,
					'mouse',
					wasSecondaryTriggered,
					props?.analyticsAttributes,
				);

				const eventAnalytics = createAnalyticsEvent({});
				fireTrackAnalytics(eventAnalytics, 'commandPalette navigation', analytics);
				fireTrackAnalytics(eventAnalytics, 'commandPalette secondaryAction', {
					...analytics,
					keyPressed: isMacOs() ? 'cmdEnter' : 'ctrlEnter',
					secondaryAction: 'openNewTab',
				});
				fireTrackAnalytics(eventAnalytics, 'commandPalette taskSucceeded', analytics);
			};

			return (
				<NavigationLink
					to={path}
					onClick={handleClick}
					{...(shouldAlwaysOpenNewTab && { target: '_blank' })}
					data-testid={NAVIGATION_LINK_TEST_ID}
					{...(props.isLinkFocused &&
						fg('dee-8683-compal-a11y-button-keyboard-accessibility') && {
							isLinkFocused: props.isLinkFocused,
						})}
				>
					<components.CommandRow {...rest} />
				</NavigationLink>
			);
		},
		[createAnalyticsEvent, getAnalyticsAttributes],
	);

	const transformNavigationCommand: TransformNavigationCommandType = useCallback(
		(command, { path, shouldAlwaysOpenNewTab }) => ({
			...command,
			components: {
				...command.components,
				Name: (base) => (
					<NameContainer>
						{command.components?.Name ? <command.components.Name {...base} /> : <>{command.name}</>}
						{!!shouldAlwaysOpenNewTab && isVisualRefreshEnabled() && (
							<Flex
								testId={ICONS_CONTAINER_TEST_ID}
								justifyContent="center"
								alignItems="center"
								xcss={iconWrapperStyles}
							>
								<ShortcutIcon label="" color="currentColor" />
							</Flex>
						)}
						{!!shouldAlwaysOpenNewTab && !isVisualRefreshEnabled() && (
							<IconContainer data-testid={ICONS_CONTAINER_TEST_ID}>
								<ShortcutIcon label="" color="currentColor" />
							</IconContainer>
						)}
					</NameContainer>
				),
				CommandRow: (base) => renderCommand(base, path, shouldAlwaysOpenNewTab),
				Footer: NewTabHintMessage,
			},
			primaryAction: {
				type: CommandActionType.PERFORM,
				perform: () => (shouldAlwaysOpenNewTab ? openNewTab(path) : openSameTab(path)),
			},
			secondaryAction: {
				type: CommandActionType.PERFORM,
				perform: () => openNewTab(path, true),
				preventModalClose: true,
				analyticsArgs: {
					secondaryAction: 'openNewTab',
				},
			},
		}),
		[openNewTab, openSameTab, renderCommand],
	);

	const shouldShowCommand = useCallback(
		(hideCommandPath: string | null): boolean => !isCurrentPage(hideCommandPath, pathname),
		[pathname],
	);

	const hideAndTransformNavigationCommand: HideAndTransformNavigationCommandType = useCallback(
		(commandToTransform) => {
			const { path, hideCommandPath, shouldAlwaysOpenNewTab, command } = commandToTransform;
			let hidePath = typeof path === 'string' ? path : path.path;

			if (hideCommandPath) hidePath = hideCommandPath;

			if (!shouldShowCommand(hidePath)) return undefined;

			return transformNavigationCommand(command, { path, shouldAlwaysOpenNewTab });
		},
		[shouldShowCommand, transformNavigationCommand],
	);

	const hideAndTransformNavigationCommands = useCallback(
		(commands: CommandToTransform[]): Command[] => {
			const filteredCommands: Command[] = [];

			commands.forEach((commandToTransform) => {
				const transformedCommand = hideAndTransformNavigationCommand(commandToTransform);
				if (transformedCommand) {
					filteredCommands.push(transformedCommand);
				}
			});

			return filteredCommands;
		},
		[hideAndTransformNavigationCommand],
	);

	return {
		shouldShowCommand,
		hideAndTransformNavigationCommand,
		hideAndTransformNavigationCommands,
		transformNavigationCommand,
	};
};

const iconWrapperStyles = xcss({
	height: '24px',
	width: '24px',
	marginLeft: 'space.075',
});
